import {
    createDataActionFactory,
    Feature,
    createActionFactory,
    createThunk,
    ThunkDispatch,
    UnifiedGroup,
} from '../../../../shared';
import { getPackageState, lpcJoinGroupCompletedAction } from '../../../../appstate';
import { packageStateKey, HubState } from '../../../state';
import { joinGroupErrorNotification } from '../../user/groups/notificationBarActions';
import joinPrivateGroup from '../../../dataLayer/ows/groups/joinPrivateGroup';
import setUnifiedGroupMembershipState from '../../../dataLayer/ows/groups/setMembershipState';
import { UnifiedGroupAccessType } from '../../../../service';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getOrigin } from 'owa-url';
import { getAccessToken } from 'owa-groups-crud-common-components/lib/utils/getAccessToken';
import type AddMembersRestRequest from 'owa-groups-services/lib/groups-rest-apis/schema/AddMembersRestRequest';
import addMembersRest from 'owa-groups-services/lib/groups-rest-apis/services/addMembersRest';

export const joinGroupClickedAction = createActionFactory<{ group: UnifiedGroup }>(
    'JoinGroupClickedAction',
    Feature.UnifiedGroups
);

export const joinGroupCompletedAction = createDataActionFactory<
    { group: UnifiedGroup; userEmailAddress: string },
    { groupId: string; groupName: string; userEmailAddress: string; hubPersonaId: string }
>('JoinGroupCompletedAction', Feature.UnifiedGroups, action => ({
    groupId: action.payload.group.externalDirectoryObjectId,
}));

export const joinGroupCompleted = (
    group: UnifiedGroup,
    userEmailAddress: string,
    isRetry?: boolean
) => async (dispatch: ThunkDispatch) => {
    if (isFeatureEnabled('grp-restApis-join')) {
        const originUrl = getOrigin();
        const accessToken = await getAccessToken(originUrl);
        const groupId = group.externalDirectoryObjectId;
        const tenantId = group.externalDirectoryOrganizationId;
        const members: string[] = [userEmailAddress];
        let joinRequest: AddMembersRestRequest = {
            Members: members,
        };

        addMembersRest(accessToken, joinRequest, groupId, tenantId)
            .then(response => {
                dispatch(
                    joinGroupCompletedAction.success({
                        group,
                        userEmailAddress,
                    })
                );
            })
            .catch((error: Error) => {
                dispatch(
                    joinGroupCompletedAction.error({
                        groupId: group.externalDirectoryObjectId,
                        groupName: group.displayName,
                        userEmailAddress,
                        hubPersonaId: group.hubPersonaId,
                    })
                );
                joinGroupErrorNotification(dispatch)(group, userEmailAddress, isRetry);
            });
    } else {
        if (group.accessType === UnifiedGroupAccessType.Private) {
            joinPrivateGroup(group.externalDirectoryObjectId, group.emailAddress, userEmailAddress)
                .then(response => {
                    dispatch(
                        joinGroupCompletedAction.success({
                            group,
                            userEmailAddress,
                        })
                    );
                })
                .catch((error: Error) => {
                    dispatch(
                        joinGroupCompletedAction.error({
                            groupId: group.externalDirectoryObjectId,
                            groupName: group.displayName,
                            userEmailAddress,
                            hubPersonaId: group.hubPersonaId,
                        })
                    );
                    joinGroupErrorNotification(dispatch)(group, userEmailAddress, isRetry);
                });
        } else {
            setUnifiedGroupMembershipState(
                group.externalDirectoryObjectId,
                group.emailAddress,
                userEmailAddress,
                'join'
            )
                .then(response => {
                    dispatch(
                        joinGroupCompletedAction.success({
                            group,
                            userEmailAddress,
                        })
                    );
                })
                .catch((error: Error) => {
                    dispatch(
                        joinGroupCompletedAction.error({
                            groupId: group.externalDirectoryObjectId,
                            groupName: group.displayName,
                            userEmailAddress,
                            hubPersonaId: group.hubPersonaId,
                        })
                    );
                    joinGroupErrorNotification(dispatch)(group, userEmailAddress, isRetry);
                });
        }
    }
};

export const lpcJoinGroupCompleted = (hubPersonaId: string) =>
    createThunk('LpcJoinGroupCompleted', (dispatch: ThunkDispatch, getState: () => {}): void => {
        const packageState = getPackageState<HubState>(getState(), packageStateKey);
        if (packageState) {
            const groupsMap = packageState.appState.data.groupsHubState.unifiedGroupsMap;

            if (groupsMap?.[hubPersonaId]) {
                const group = groupsMap[hubPersonaId];
                dispatch(
                    lpcJoinGroupCompletedAction({
                        groupId: group.externalDirectoryObjectId,
                        groupName: group.displayName,
                        userEmailAddress:
                            packageState.appState.data.userConfiguration.settings.UserEmailAddress,
                        accessType: group.accessType,
                        hubPersonaId: group.hubPersonaId,
                    })
                );
            }
        }
    });

export const lpcJoinGroupError = (hubPersonaId: string) =>
    createThunk('LpcJoinGroupError', (dispatch: ThunkDispatch, getState: () => {}): void => {
        const packageState = getPackageState<HubState>(getState(), packageStateKey);
        if (packageState) {
            const groupsMap = packageState.appState.data.groupsHubState.unifiedGroupsMap;
            const userEmailAddress =
                packageState.appState.data.userConfiguration.settings.UserEmailAddress;
            const isRetry = false;
            if (groupsMap?.[hubPersonaId]) {
                joinGroupErrorNotification(dispatch)(
                    groupsMap[hubPersonaId],
                    userEmailAddress,
                    isRetry
                );
            }
        }
    });
